import { SITE } from "@/lib/site-data";
import { HOURLY_RATES, VEHICLE_CLASSES, ratesByClass } from "@/lib/pricing";
import { PRICING_FAQS } from "@/lib/pricing-faq";
import { ContactButtons } from "@/components/ContactButtons";
import PriceGuideDownload from "@/components/PriceGuideDownload";

type Props = {
  heading?: string;
  intro?: string;
  className?: string;
  /** "cards" = compact grid, "table" = per-vehicle-class rate tables */
  layout?: "cards" | "table";
  /** Render the pricing FAQ accordion. Off on pages that already carry a page-level FAQ. */
  showFaqs?: boolean;

};

export function HourlyRates({
  heading = "Hourly as-directed rates",
  intro = "Transparent published hourly pricing. Every hourly booking carries a five-hour minimum; tolls, parking, airport fees, and gratuity treatment are shown in writing, and gratuity is excluded unless the confirmation says otherwise.",
  className = "",
  layout = "cards",
  showFaqs = true,

}: Props) {
  return (
    <section id="rates" className={`border-t border-border ${className}`}>
      <div className="container-x py-20">
        <p className="eyebrow mb-3 text-gold">Pricing</p>
        <h2 className="text-3xl md:text-4xl mb-4">{heading}</h2>
        <p className="text-muted-foreground max-w-3xl mb-10">{intro}</p>

        {layout === "table" ? (
          <div className="grid gap-8 lg:grid-cols-3">
            {VEHICLE_CLASSES.map((vc) => (
              <div key={vc.name} className="border border-border">
                <div className="border-b border-border p-6">
                  <h3 className="text-xl mb-2">{vc.name}</h3>
                  <p className="text-sm text-muted-foreground">{vc.blurb}</p>
                </div>
                <div
                  className="overflow-x-auto focus-visible:outline-2 focus-visible:outline-gold"
                  tabIndex={0}
                  role="region"
                  aria-label={`${vc.name} hourly rates`}
                >
                  <table className="w-full text-sm">
                    <caption className="sr-only">
                      {vc.name} hourly as-directed rates by passenger capacity
                    </caption>
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                        <th scope="col" className="px-6 py-3 font-normal">Vehicle</th>
                        <th scope="col" className="px-6 py-3 font-normal">Capacity</th>
                        <th scope="col" className="px-6 py-3 font-normal text-right">Hourly</th>
                        <th scope="col" className="px-6 py-3 font-normal text-right">Min.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ratesByClass(vc.name).map((r) => (
                        <tr key={`${r.vehicle}-${r.capacity}`} className="border-t border-border align-top">
                          <th scope="row" className="px-6 py-4 font-normal text-foreground text-left">
                            {r.vehicle}
                          </th>
                          <td className="px-6 py-4 text-muted-foreground">{r.capacity}</td>
                          <td className="px-6 py-4 text-right text-gold whitespace-nowrap">${r.rate}/hr</td>
                          <td className="px-6 py-4 text-right text-muted-foreground whitespace-nowrap">
                            {r.minimumHours} hrs
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {HOURLY_RATES.map((r) => (
              <article
                key={`${r.vehicle}-${r.capacity}`}
                className="border border-border p-6 hover:border-gold transition-colors"
              >
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{r.capacity}</p>
                <p className="font-display text-xl mb-3">{r.vehicle}</p>
                <p className="text-3xl text-gold mb-1">
                  ${r.rate}
                  <span className="text-base text-muted-foreground"> / hour</span>
                </p>
                <p className="text-xs text-muted-foreground mb-4">{r.minimumHours}-hour minimum</p>
                <p className="text-sm text-muted-foreground">{r.note}</p>
              </article>
            ))}
          </div>
        )}

        <div className="mt-10 border border-border p-6 md:p-8">
          <p className="eyebrow mb-3 text-gold">One-way transfers</p>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Hourly rates above apply to as-directed bookings. For a{" "}
            <strong className="text-foreground">point-to-point or city-to-city one-way transfer</strong>, check our{" "}
            <a href={SITE.bookUrl} target="_blank" rel="noopener noreferrer" className="text-gold underline underline-offset-2">
              instant price on our website
            </a>{" "}
            — enter your pickup and drop-off and you will see a flat all-in quote in seconds, with no hourly minimum.
          </p>

          <ContactButtons className="mt-6" showBook={false} context="hourly charter rates" />

          <div className="mt-4">
            <PriceGuideDownload withPreview context="hourly rates" />
            <p className="mt-2 text-xs text-muted-foreground">
              PDF includes hourly rates by vehicle class, airport coverage, popular one-way routes and a 51-city service summary.
            </p>
          </div>

          <p className="mt-4 text-xs text-muted-foreground">
            Text and WhatsApp: {SITE.text} · Phone dispatch: {SITE.phone} — available daily.
          </p>
        </div>

        {/* PRICING FAQ — visible answers + FAQPage structured data */}
        {showFaqs && (
          <div className="mt-12">
            <h3 className="text-2xl mb-6">Pricing and instant-quote questions</h3>
            <div className="grid gap-4 md:grid-cols-2">
              {PRICING_FAQS.map((f) => (
                <details key={f.q} className="border border-border p-5 group">
                  <summary className="cursor-pointer text-sm text-foreground marker:text-gold">{f.q}</summary>
                  <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        )}



      </div>
    </section>
  );
}
